import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ChevronRight, Search, Tag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { allBlogPosts } from '../data/blogData';
import SEOHead from '../components/SEOHead';
import { companyInfo, serviceAreas } from '../data/mock';

const BlogListPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get unique categories
  const categories = ['all', ...new Set(allBlogPosts.map(post => post.category))];

  // Filter posts
  const filteredPosts = allBlogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.metaDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Schema for blog list
  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Gray Contracting DFW Blog",
    "description": "Expert guides and tips for home improvement in Dallas-Fort Worth. Painting, remodeling, roofing, masonry, and more from 55+ years of experience.",
    "url": "https://graycontractingdfw.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Gray Contracting DFW",
      "url": "https://graycontractingdfw.com"
    },
    "blogPost": allBlogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `https://graycontractingdfw.com/blog/${post.slug}`,
      "datePublished": post.publishDate,
      "author": { "@type": "Organization", "name": post.author }
    }))
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": companyInfo.name,
    "url": "https://graycontractingdfw.com",
    "telephone": companyInfo.phone,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": companyInfo.addressStreet,
      "addressLocality": companyInfo.addressCity,
      "addressRegion": companyInfo.addressState,
      "postalCode": companyInfo.addressZip
    },
    "areaServed": serviceAreas.map(area => ({ "@type": "City", "name": area }))
  };

  return (
    <div className="bg-white min-h-screen">
      <SEOHead 
        title="Home Improvement Blog Dallas TX | Expert Tips & Guides | Gray Contracting"
        description="Expert home improvement guides for Dallas homeowners. Tips on painting, remodeling, roofing, masonry, and more from contractors with 55+ years of DFW experience."
        keywords="Dallas home improvement blog, painting tips Dallas, remodeling guide DFW, roofing advice Texas"
        canonical="/blog"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogListSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />

      {/* Hero */}
      <section className="bg-neutral-900 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-6">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-amber-400">Blog</span>
          </nav>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Dallas Home Improvement Blog
          </h1>
          <p className="text-neutral-400 text-lg max-w-2xl">
            Expert guides, tips, and insights from 55+ years of serving Dallas-Fort Worth homeowners. 
            Find everything you need to know about painting, remodeling, roofing, and more.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b sticky top-0 bg-white z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input 
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {cat === 'all' ? 'All Topics' : cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-neutral-500">No articles found matching your search.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map(post => (
                <article 
                  key={post.id}
                  className="bg-white rounded-xl overflow-hidden border hover:shadow-lg transition-shadow group"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div 
                      className="h-48 bg-cover bg-center group-hover:scale-105 transition-transform duration-300"
                      style={{ backgroundImage: `url('${post.heroImage}')` }}
                    />
                  </Link>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded">
                        {post.category}
                      </span>
                      <span className="text-xs text-neutral-500">{post.readTime}</span>
                    </div>
                    
                    <Link to={`/blog/${post.slug}`}>
                      <h2 className="text-lg font-bold text-neutral-900 group-hover:text-amber-600 transition-colors mb-2 line-clamp-2">
                        {post.title.split('|')[0].trim()}
                      </h2>
                    </Link>
                    
                    <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
                      {post.metaDescription.substring(0, 120)}...
                    </p>

                    <div className="flex items-center justify-between text-xs text-neutral-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <Link 
                        to={`/blog/${post.slug}`}
                        className="flex items-center gap-1 text-amber-600 font-medium hover:text-amber-700"
                      >
                        Read More <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-neutral-100 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">
            Need Professional Help With Your Project?
          </h2>
          <p className="text-neutral-600 mb-6">
            Our team of experts is ready to help with your home improvement needs. 
            Free estimates available for Dallas-Fort Worth homeowners.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600">
              <Link to="/contact">Get Free Estimate</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-8 border-t">
        <div className="max-w-6xl mx-auto px-4">
          <p className="text-sm text-neutral-500 text-center">
            <strong>Serving:</strong> {serviceAreas.join(' • ')} and surrounding Dallas-Fort Worth areas
          </p>
        </div>
      </section>
    </div>
  );
};

export default BlogListPage;
